import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGmailAuth } from './hooks/useGmailAuth';
import { ConfigScreen } from './components/ConfigScreen';
import { ProgressScreen } from './components/ProgressScreen';
import './GmailExtractor.css';

type Screen = 'config' | 'progress';

const GmailExtractor: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [screen, setScreen] = useState<Screen>('config');
  const [extractionParams, setExtractionParams] = useState<{
    senders: string[];
    maxParallel: number;
    dateAfter: string;
    dateBefore: string;
  } | null>(null);

  const auth = useGmailAuth();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (screen === 'progress') {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [screen]);

  const handleStart = (
    senders: string[],
    maxParallel: number,
    dateAfter: string,
    dateBefore: string
  ) => {
    setExtractionParams({ senders, maxParallel, dateAfter, dateBefore });
    setScreen('progress');
  };

  const handleComplete = () => {
    setExtractionParams(null);
    setScreen('config');
  };

  if (auth.loading) {
    return (
      <div className="ge-page">
        <div className="ge-bg">
          <div className="ge-nebula ge-nebula-1" />
          <div className="ge-nebula ge-nebula-2" />
          <div className="ge-nebula ge-nebula-3" />
        </div>
        <div className={`ge-content ${isVisible ? 'visible' : ''}`}>
          <nav className="ge-nav">
            <Link to="/" className="ge-back">
              ← Back to Metlas
            </Link>
          </nav>
          <p className="ge-loading">Loading...</p>
        </div>
        <footer className="ge-footer">
          <div className="ge-footer-inner">
            <span className="ge-footer-copy">© 2026 Metlas, Inc.</span>
            <div className="ge-footer-links">
              <Link to="/privacy-terms" className="ge-footer-link">
                Terms
              </Link>
              <Link to="/privacy-terms" className="ge-footer-link">
                Privacy
              </Link>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="ge-page">
      <div className="ge-bg">
        <div className="ge-nebula ge-nebula-1" />
        <div className="ge-nebula ge-nebula-2" />
        <div className="ge-nebula ge-nebula-3" />
      </div>

      <div className={`ge-content ${isVisible ? 'visible' : ''}`}>
        <nav className="ge-nav">
          <Link to="/" className="ge-back">
            ← Back to Metlas
          </Link>
        </nav>

        {screen === 'config' ? (
          <>
            <header className="ge-hero">
              <h1 className="ge-headline">Metlas Gmail Extractor</h1>
              <p className="ge-subheadline">
                Download email threads that contain any of the provided email addresses (in From,
                To, Cc, or Bcc), or all if no email addresses are provided. All extraction runs in
                your browser—no emails are stored or exposed on our servers.
              </p>
            </header>
            <ConfigScreen
            profile={auth.profile}
            isSignedIn={auth.isSignedIn}
            loading={auth.loading}
            authError={auth.error}
            onSignIn={auth.signIn}
            onSignOut={auth.signOut}
            onStart={handleStart}
          />
          </>
        ) : extractionParams ? (
          <ProgressScreen
            senders={extractionParams.senders}
            maxParallel={extractionParams.maxParallel}
            dateAfter={extractionParams.dateAfter}
            dateBefore={extractionParams.dateBefore}
            getAccessToken={auth.getAccessToken}
            onComplete={handleComplete}
          />
        ) : null}
      </div>

      <footer className="ge-footer">
        <div className="ge-footer-inner">
          <span className="ge-footer-copy">© 2026 Metlas, Inc.</span>
          <div className="ge-footer-links">
            <Link to="/privacy-terms" className="ge-footer-link">
              Terms
            </Link>
            <Link to="/privacy-terms" className="ge-footer-link">
              Privacy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GmailExtractor;
